import React, { useEffect } from "react";
import { itinerarySelector } from "redux/selectedItinerary";
import { useDispatch, useSelector } from "react-redux";
import {
  setPaymentMethod,
  getCheapestPaymentMethod,
  shoppingCartSelector,
} from "redux/shoppingCart";
import { useTranslations } from "hooks/useTranslations";
import { ConfirmBooking } from "../ConfirmBooking/ConfirmBooking";
import { checkoutStepsSelector } from "redux/checkoutSteps";
import { MediaQuery, formatPrice, useConfig } from "@thekilroygroup/shared-ui";
import { Box, Typography, SxProps, Divider } from "@mui/material";
import { PaymentRadioGroup } from "./PaymentRadioGroup";
import { TermsAndConditions } from "./TermsAndConditions";
import { Summary } from "./PaymentSummary";
import { PaymentInfoList } from "./PaymentInfoList";
import { PaymentDisclaimer } from "./PaymentDisclaimer";

interface IPaymentMethodSelectorProps {
  validateHandler: (valid: boolean) => void;
}

export default function PaymentMethodSelector(
  props: IPaymentMethodSelectorProps,
) {
  const { validateHandler } = props;

  const {
    translations: {
      paymentMethod: { title, description },
    },
  } = useTranslations();

  const { locale } = useConfig();

  const dispatch = useDispatch();
  const itinerary = useSelector(itinerarySelector);

  const shoppingCart = useSelector(shoppingCartSelector);
  const cheapestPaymentMethod = getCheapestPaymentMethod(itinerary);
  const checkoutSteps = useSelector(checkoutStepsSelector);

  useEffect(() => {
    const initialMethod = itinerary.paymentMethods.reduce(
      (cheapestPaymentMethod, paymentMethod) =>
        paymentMethod.fee < cheapestPaymentMethod.fee
          ? paymentMethod
          : cheapestPaymentMethod,
    );
    dispatch(setPaymentMethod(initialMethod));
  }, [itinerary]);

  useEffect(() => {
    const isValid =
      !!shoppingCart.selectedPaymentMethod &&
      !!shoppingCart.acceptedTermsAndConditions;
    validateHandler(isValid);
  }, [
    shoppingCart.selectedPaymentMethod,
    shoppingCart.acceptedTermsAndConditions,
  ]);

  const getPopulatedDescription = (): string => {
    const price = cheapestPaymentMethod?.discount.amount;
    const currency = cheapestPaymentMethod?.discount.currency;

    return description.replace(
      "{amount}",
      formatPrice(price, locale, currency as string),
    );
  };

  return (
    <Box sx={sx.wrapper}>
      <MediaQuery max="md">
        <Box className="tabletTitleWrapper" sx={sx.tabletTitleWrapper}>
          <Typography variant="titleMedium" weight="bold" sx={sx.title}>
            {title}
          </Typography>
          <Typography variant="bodyMedium">
            {getPopulatedDescription()}
          </Typography>
        </Box>
      </MediaQuery>
      <PaymentRadioGroup itinerary={itinerary} />
      <Divider sx={sx.divider} />
      <Summary itinerary={itinerary} />
      <TermsAndConditions sxProps={{ order: { md: 2 } }} />
      <ConfirmBooking
        sx={{ order: { md: 2 } }}
        isValid={checkoutSteps.allStepsAreValid}
      />
      <PaymentInfoList />
      <PaymentDisclaimer />
    </Box>
  );
}

const sx: { [key: string]: SxProps } = {
  wrapper: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
  },
  title: {
    color: "primary.main",
    mb: 1,
  },
  termsAndConditions: {
    order: { md: 2 },
  },
  tabletTitleWrapper: {
    p: 2.5,
    px: 2,
  },
  divider: { mx: { xs: 2, md: 0 }, my: { md: 2 }, borderColor: "primary.main" },
};
